import { useAuthenticator } from "@aws-amplify/ui-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import useClearSWR from "./useClearSWR"
import { useAppDispatch } from "./useDispatch"
import {
    IS_AUTHENTICATED,
    IS_AUTH_CONFIGURING,
} from "../constants/generalConstants"
import {
    DNO_ADMIN,
    ENA_SYS_ADMIN,
    INSTALLER_ADMIN,
    LCT_MANU_ADMIN,
    PUBLIC,
    DISPLAY_ROLES,
    IDENTIFIER_TYPE_DNO_TAG,
    IDENTIFIER_TYPE_LCT_MANU_TAG,
    IDENTIFIER_TYPE_INSTALLER_TAG,
    DNO_APPROVED,
    INSTALLER_APPROVED,
    LCT_MANU_APPROVED,
    DB_ASSESSOR_GEN,
    INSTALLER,
    DB_ASSESSOR_DEMAND,
    AWAITING_SET_UP,
} from "../constants/roles"
import { setShowAccountSetUp } from "../redux/preferencesSlice"
import { CLOSED } from "../redux/types"
import { SIGN_OUT_SUCCESS } from "../router/routes"
import { arraysAreIdentical } from "../utils/general/generalUtils"

const COGNITO_GROUPS_KW = "cognito:groups"
const CUSTOM_ORG_SET_UP_ATTR = "custom:OrgSetUp"
const PENDING_ATTR_VALUE = "PENDING"
const NO_MFA_GROUPS = [INSTALLER]

/**
 * Cognito User Interface
 *
 * @interface
 * <ICognitoUser>
 */
interface ICognitoUser {
    Session: any
    authenticationFlowType: string
    preferredMFA: string
}

export interface IUserData {
    groups: string[]
    displayGroups: string[]
    firstName: string
    lastName: string
    hasSetMfa: boolean
    email: string
    username: string
    isAwaitingSetup: boolean
    isSetUpPending: boolean
    isSignedIn: boolean
    isConfiguring: boolean
    isMfaRequired: boolean
    isDnoApproved: boolean
    isDnoAdmin: boolean
    isInstallerAdmin: boolean
    isInstaller: boolean
    isManuAdmin: boolean
    isEnaSysAdmin: boolean
    isManuApproved: boolean
    isGenAssessor: boolean
    isDemandAssessor: boolean
    showUnapprovedWarning: boolean
    isDnoUnapproved: boolean
    isInstallerUnapproved: boolean
    isManuUnapproved: boolean
    onSignOut: () => void
    isManufacturerForDevice: (deviceRef: string) => boolean
}

/**
 * Hook for extracting user's data from `useAuthenticator` hook and user object.
 * If user is not authenticated, `["PUBLIC"]` is returned for cognito groups, and
 * empty string for attribute fields. A user with no groups is given public access
 * (note this scenario is invalid and group management APIs should block this).
 * @returns {object} - object with user's groups, first name, last name, email, username
 * and flags for if user is signed in and if auth is configuring
 */
export const useGetUserData = (): IUserData => {
    const { clearCache } = useClearSWR()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { user, authStatus, signOut } = useAuthenticator((context) => [
        context.user,
    ])

    let groups = [PUBLIC]
    let firstName = ""
    let lastName = ""
    let email = ""
    let username = ""
    let tokenGroups = []
    const [isSetUpPending, setIsSetUpPending] = useState(false)
    const payload = user
        ? user.getSignInUserSession()?.getAccessToken()?.payload
        : null

    const isSignedIn = authStatus === IS_AUTHENTICATED
    const isConfiguring = authStatus === IS_AUTH_CONFIGURING

    if (payload && isSignedIn) {
        tokenGroups = payload[COGNITO_GROUPS_KW] || []
        groups = tokenGroups || [PUBLIC]
        firstName = user.attributes?.given_name || ""
        lastName = user.attributes?.family_name || ""
        email = user.attributes?.email || ""
        username = user.attributes?.sub || ""
    }

    useEffect(() => {
        if (user) {
            const userPending =
                user?.attributes?.[CUSTOM_ORG_SET_UP_ATTR] ===
                PENDING_ATTR_VALUE
            setIsSetUpPending(userPending)
        } else {
            setIsSetUpPending(false)
        }
    }, [user])

    const isMfaRequired =
        (user as unknown as ICognitoUser)?.preferredMFA === "NOMFA" &&
        !arraysAreIdentical(tokenGroups, NO_MFA_GROUPS)

    const hasSetMfa =
        (user as unknown as ICognitoUser)?.preferredMFA === "SOFTWARE_TOKEN_MFA"

    const onSignOut = async (): Promise<void> => {
        await clearCache()
        signOut({ global: true })
        navigate(SIGN_OUT_SUCCESS)
        // Reset account set up state to closed. This ensures that if the user
        // creates a new account, they will be shown the account set up
        // instruction dialogue on sign up.
        dispatch(setShowAccountSetUp(CLOSED))
    }

    const isEnaSysAdmin = groups.includes(ENA_SYS_ADMIN)
    const isDnoAdmin = groups.includes(DNO_ADMIN)
    const isDnoApproved = groups.includes(DNO_APPROVED)
    const isInstallerAdmin = groups.includes(INSTALLER_ADMIN)
    const isManuAdmin = groups.includes(LCT_MANU_ADMIN)
    const isManuApproved = groups.includes(LCT_MANU_APPROVED)
    const isGenAssessor = groups.includes(DB_ASSESSOR_GEN)
    const isDemandAssessor = groups.includes(DB_ASSESSOR_DEMAND)
    const isAwaitingSetup = groups.includes(AWAITING_SET_UP)
    const isInstaller = groups.includes(INSTALLER)

    // Create array of display groups. If group is not in DISPLAY_ROLES, remove
    // any identifier tags and return the group name. If group is identifier
    // type, remove identifier tag.
    const displayGroups = groups.map((group) => {
        let displayGroup = DISPLAY_ROLES[group] ?? group
        if (displayGroup.startsWith(IDENTIFIER_TYPE_DNO_TAG)) {
            displayGroup = displayGroup.replace(IDENTIFIER_TYPE_DNO_TAG, "")
        } else if (displayGroup.startsWith(IDENTIFIER_TYPE_INSTALLER_TAG)) {
            displayGroup = displayGroup.replace(
                IDENTIFIER_TYPE_INSTALLER_TAG,
                ""
            )
        } else if (displayGroup.startsWith(IDENTIFIER_TYPE_LCT_MANU_TAG)) {
            displayGroup = displayGroup.replace(
                IDENTIFIER_TYPE_LCT_MANU_TAG,
                ""
            )
        }
        return displayGroup
    })

    // Extract unapproved state for all major user bases
    const isDnoUnapproved =
        groups.some((group) => group.includes(IDENTIFIER_TYPE_DNO_TAG)) &&
        !groups.includes(DNO_APPROVED)

    const isInstallerUnapproved =
        groups.some((group) => group.includes(IDENTIFIER_TYPE_INSTALLER_TAG)) &&
        !groups.includes(INSTALLER_APPROVED)

    const isManuUnapproved =
        groups.some((group) => group.includes(IDENTIFIER_TYPE_LCT_MANU_TAG)) &&
        !groups.includes(LCT_MANU_APPROVED)

    // Show unapproved warning if user is unapproved and has set up MFA.
    const showUnapprovedWarning =
        (isDnoUnapproved || isInstallerUnapproved || isManuUnapproved) &&
        !isMfaRequired

    const isManufacturerForDevice = (deviceRef: string): boolean => {
        const [deviceManufacturer] = deviceRef.split("/")
        const requestorManufacturer =
            groups
                .find((role) => role.startsWith(IDENTIFIER_TYPE_LCT_MANU_TAG))
                ?.split(":")[2] ?? undefined
        return requestorManufacturer === deviceManufacturer
    }

    return {
        groups,
        displayGroups,
        firstName,
        lastName,
        hasSetMfa,
        email,
        username,
        isAwaitingSetup, // Awaiting manual set up
        isSetUpPending, // First log in so need to go through account set up process
        isSignedIn,
        isConfiguring,
        isMfaRequired,
        isDnoAdmin,
        isDnoApproved,
        isInstallerAdmin,
        isInstaller,
        isEnaSysAdmin,
        isManuAdmin,
        isManuApproved,
        isGenAssessor,
        isDemandAssessor,
        showUnapprovedWarning,
        isDnoUnapproved,
        isInstallerUnapproved,
        isManuUnapproved,
        onSignOut,
        isManufacturerForDevice,
    }
}
